import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { t } from '../../../lib/i18n';
import { clientFinanceKey, fetchClientFinance } from '../api';
import { type User } from '../types';
import { ClientTierCard } from './client-tier-card';
import { AddPaymentDialog } from './finances/add-payment-dialog';
import { ClientFinanceSummary } from './finances/client-finance-summary';
import { ClientInvoicesTable } from './finances/client-invoices-table';
import { DepositActions } from './finances/deposit-actions';
import { LegacyArchive } from './finances/legacy-archive';
import { MoneyJournal } from './finances/money-journal';
import { OpenTopUpBanner } from './finances/open-topup-banner';

/** Which invoice «Оплатити» preselected; null = opened from the summary. */
type PaymentDialogState = { invoiceNo: string | null } | null;

/**
 * Client «Фінанси» tab — ONE way in for client money (task
 * client-finances-rethink, SPEC §4).
 *
 * Everything renders from one read, `GET /admin/users/:id/finance`:
 *  1. summary — deposit, invoice debt, overdue, bid ceiling, «+ Додати платіж»,
 *     then «Рівень комісії» (volume tiers, its own read — `ClientTierCard`);
 *  2. the open top-up request banner (only when one exists);
 *  3. «Рахунки» with «Оплатити» (while `PAYMENTS_ENABLED` is on);
 *  4. «Рух грошей» — payments grouped with their allocations;
 *  5. «Дії з депозитом» — Коригування (reason required) · refund pointer;
 *  6. the collapsed read-only «Архів (стара система)».
 *
 * The browser computes no balance: the old «Огляд балансу», «Історія платежів»,
 * «Витрати на виграні автомобілі» and the deposit top-up form are gone.
 */
export function UserFinancesTab({ user }: { user: User }) {
  const userId = user._id;
  const [dialog, setDialog] = useState<PaymentDialogState>(null);

  const query = useQuery({
    queryKey: clientFinanceKey(userId ?? ''),
    queryFn: () => fetchClientFinance(userId as string),
    enabled: !!userId,
  });

  if (!userId) return <Notice text={t('clients.finance.failed')} />;
  if (query.isPending) return <Notice text={t('common.loading.data')} />;
  if (query.isError) return <Notice text={t('clients.finance.failed')} />;

  const finance = query.data;

  return (
    <div className="flex flex-col gap-3 lg:gap-5">
      <ClientFinanceSummary
        summary={finance.summary}
        onAddPayment={() => setDialog({ invoiceNo: null })}
      />
      <ClientTierCard userId={userId} planEvaluatedAt={user.planEvaluatedAt ?? null} />
      <OpenTopUpBanner requests={finance.openTopUpRequests} />
      <ClientInvoicesTable
        invoices={finance.invoices}
        paymentsEnabled={finance.paymentsEnabled}
        onPay={(invoiceNo) => setDialog({ invoiceNo })}
      />
      <MoneyJournal journal={finance.journal} />
      <DepositActions userId={userId} />
      <LegacyArchive userId={userId} count={finance.archive.count} />
      {dialog && (
        <AddPaymentDialog
          userId={userId}
          finance={finance}
          initialInvoiceNo={dialog.invoiceNo}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

function Notice({ text }: { text: string }) {
  return (
    <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-10 text-center text-[13px] text-cf-ink-subtle">
      {text}
    </section>
  );
}
